import { Suspense, lazy, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { KineticLines } from "./Reveal";

const Scene3D = lazy(() => import("./Scene3D").then((m) => ({ default: m.Scene3D })));

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const sceneOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} id="top" className="relative min-h-[100vh] w-full overflow-hidden bg-ink-950" data-testid="hero-section">
      {/* 3D scene */}
      <motion.div style={{ opacity: sceneOpacity }} className="absolute inset-0 z-0">
        <Suspense fallback={<div className="absolute inset-0 bg-ink-950" />}>
          <Scene3D />
        </Suspense>
      </motion.div>

      {/* Grid + legibility gradients */}
      <div className="pointer-events-none absolute inset-0 z-[1] grid-lines opacity-30" />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-r from-ink-950 via-ink-950/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-40 bg-gradient-to-t from-ink-950 to-transparent" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="pointer-events-none relative z-10 mx-auto flex min-h-[100vh] max-w-7xl flex-col justify-center px-6 pt-32 pb-24 md:px-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="pointer-events-auto mb-8 inline-flex w-fit items-center gap-2 rounded-full border border-white/10 glass px-4 py-1.5"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-brand-green shadow-[0_0_10px_#00FF66]" />
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-300">
            AI-Driven IT Consulting &amp; Engineering
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-display text-5xl font-black leading-[0.98] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-[5.5rem]">
          <KineticLines delay={0.4} lines={["Engineering Tomorrow's", "Intelligent Digital"]} />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-brand text-glow"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 + 2 * 0.12 }}
            >
              Infrastructure
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-300 sm:text-lg"
        >
          We deliver AI-powered automation, telecom solutions, cloud platforms, cybersecurity, and
          enterprise software that help organizations innovate faster and operate smarter.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="pointer-events-auto mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button
            onClick={() => go("#contact")}
            data-testid="hero-cta-talk"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-ink-950 transition-transform duration-300 hover:scale-[1.03] glow-cyan"
          >
            Talk to Our Experts
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => go("#solutions")}
            data-testid="hero-cta-solutions"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-4 text-sm font-semibold text-white backdrop-blur transition-colors duration-300 hover:border-brand/50 hover:bg-white/10"
          >
            Explore Solutions
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
        >
          {[
            ["11+", "Solution Domains"],
            ["End-to-End", "Delivery Capability"],
            ["Enterprise", "Grade Engineering"],
          ].map(([k, v]) => (
            <div key={v}>
              <div className="font-display text-2xl font-bold text-white sm:text-3xl">{k}</div>
              <div className="mt-1 text-xs text-zinc-500 sm:text-sm">{v}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-brand to-transparent"
        />
      </motion.div>
    </section>
  );
};
